import * as React from 'react';
import { AppProvider } from '@toolpad/core/AppProvider';
import dashboardTheme from '../theme/dashboardTheme';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid2';
import { Stack, Typography, useMediaQuery } from '@mui/material';
import Countdown from 'react-countdown';
import PlayersTable from './PlayersTable';
import SetView from './SetView';
import { colors } from '../theme/colors';
import { format } from 'date-fns';

import { getViewData } from '../services/view';
import ViewData from '../models/view';
import Set from '../models/set';
import Player from '../models/player';

export default function Dashboard() {
  const [data, setData] = React.useState<ViewData | null>(null);
  const [currentSet, setCurrentSet] = React.useState<Set | undefined>(undefined);
  const [nextSets, setNextSets] = React.useState<Set[]>([]);

  React.useEffect(() => {
    async function getData() {
      setData(await getViewData());
    }
    getData();

    const interval = setInterval(getData, 15000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  React.useEffect(() => {
    if (data && data.marathon.currentSet) {
      const currentSet = data.sets.find((set) => set.setNumber === data.marathon.currentSet);
      const nextSets = data.sets.filter(
        // For some reason, TypeScript is not able to guarantee that data.marathon.currentSet
        // is not undefined here even though it's checked 4 lines above. So, we need to explicitly
        // check that again.
        (set) => data.marathon.currentSet && set.setNumber > data.marathon.currentSet,
      );
      setCurrentSet(currentSet);
      setNextSets(nextSets);
    }
  }, [data]);

  function sortPlayersByRatio(PlayerData: Player[]): Player[] {
    return [...PlayerData].sort((a, b) => {
      // Primary sorting: by ratio (descending)
      const ratioA = a.ratio ?? 0;
      const ratioB = b.ratio ?? 0;

      if (ratioA === ratioB) {
        // Secondary sorting: by setsPlayed (descending) when ratios are equal or null
        return (b.setsPlayed ?? 0) - (a.setsPlayed ?? 0);
      }

      return ratioB - ratioA;
    });
  }

  function filterPlayersWithTeam(players: Player[]): Player[] {
    return players.filter((player) => player.team === 'c' || player.team === 'm');
  }

  const isSmallScreen = useMediaQuery(dashboardTheme.breakpoints.down('sm'));

  return (
    <AppProvider
      theme={dashboardTheme}
      branding={{
        logo: <img src="./sokol-logo.png" alt="Sokol logo" style={{ marginRight: 10 }} />,
        title: isSmallScreen ? '24-ka' : '15. volejbalový maratón, memoriál Jaromíra Šinku',
      }}
    >
      <DashboardLayout hideNavigation>
        <PageContainer breadcrumbs={[]} title={''}>
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2 }}
            sx={{
              mb: { xs: 5 },
            }}
            columns={{ md: 12 }}
          >
            <Grid size={{ xs: 1, md: 4 }}>
              <Stack
                direction="column"
                spacing={{ xs: 0, md: 1 }}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  align="center"
                  sx={{
                    typography: { xs: 'h5', md: 'h4' },
                  }}
                >
                  Štart
                </Typography>
                <Typography
                  variant="h4"
                  align="center"
                  sx={{
                    typography: { xs: 'h5', md: 'h4' },
                  }}
                >
                  {data && data.marathon.startDate
                    ? format(data.marathon.startDate, 'dd.MM.yyyy HH:mm')
                    : 'čaká sa na začiatok'}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={4}>
              <Typography
                variant="h4"
                sx={{
                  typography: { xs: 'h3', md: 'h2' },
                  '@media (min-width:300px)': { fontWeight: 700 },
                }}
              >
                {data?.marathon.endDate && (
                  <Countdown
                    date={data.marathon.endDate}
                    daysInHours={true}
                    renderer={
                      data?.marathon.startDate && new Date() < data.marathon.startDate
                        ? () => {
                            return <span>24:00:00</span>;
                          }
                        : undefined
                    }
                  />
                )}
              </Typography>
            </Grid>
            <Grid size={4}>
              <Stack
                direction="column"
                spacing={{ xs: 0, md: 1 }}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  align="center"
                  sx={{
                    typography: { xs: 'h5', md: 'h4' },
                  }}
                >
                  Koniec
                </Typography>
                <Typography
                  variant="h4"
                  align="center"
                  sx={{
                    typography: { xs: 'h5', md: 'h4' },
                  }}
                >
                  {data && data.marathon.endDate
                    ? format(data.marathon.endDate, 'dd.MM.yyyy HH:mm')
                    : 'čaká sa na začiatok'}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent="center"
            alignItems={{ xs: 'center', md: 'flex-start' }}
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2 }}
            sx={{
              mb: { xs: 15 },
            }}
            columns={{ md: 12 }}
          >
            <Grid size={4} order={{ xs: 2, md: 1 }}>
              <Stack
                direction="column"
                spacing={{ xs: 1 }}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h5"
                  align="center"
                  sx={{
                    typography: { xs: 'h5', md: 'h4' },
                    '@media (min-width:300px)': { fontWeight: 700 },
                  }}
                >
                  Nasledujúce sety
                </Typography>
                {nextSets.map((set) => (
                  <SetView SetData={set} key={set.setNumber} />
                ))}
              </Stack>
            </Grid>
            <Grid size={4} order={{ xs: 1, md: 2 }}>
              <Stack
                direction="column"
                spacing={{ xs: 1 }}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction={'row'}
                  spacing={{ xs: 2 }}
                  sx={{
                    mb: { xs: 1 },
                  }}
                  columns={{ md: 12 }}
                >
                  <Typography
                    sx={{
                      color: colors.defaultRedDark,
                      typography: { xs: 'h1' },
                      '@media (min-width:300px)': { fontWeight: 700 },
                    }}
                  >
                    {data?.marathon?.setsRed || 0}
                  </Typography>
                  <Typography
                    sx={{
                      typography: { xs: 'h1' },
                      '@media (min-width:300px)': { fontWeight: 700 },
                    }}
                  >
                    {' : '}
                  </Typography>
                  <Typography
                    sx={{
                      color: colors.defaultBlueDark,
                      typography: { xs: 'h1' },
                      '@media (min-width:300px)': { fontWeight: 700 },
                    }}
                  >
                    {data?.marathon?.setsBlue || 0}
                  </Typography>
                </Grid>
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction={'row'}
                  spacing={{ xs: 1 }}
                  sx={{ paddingBottom: 3 }}
                >
                  <Typography
                    sx={{
                      color: colors.defaultRedDark,
                      typography: { xs: 'h4', md: 'h3' },
                      '@media (min-width:300px)': { fontWeight: 700 },
                    }}
                  >
                    {data?.marathon?.pointsRed || 0}
                  </Typography>
                  <Typography
                    sx={{
                      typography: { xs: 'h4', md: 'h3' },
                      '@media (min-width:300px)': { fontWeight: 700 },
                    }}
                  >
                    {' : '}
                  </Typography>
                  <Typography
                    sx={{
                      color: colors.defaultBlueDark,
                      typography: { xs: 'h4', md: 'h3' },
                      '@media (min-width:300px)': { fontWeight: 700 },
                    }}
                  >
                    {data?.marathon?.pointsBlue || 0}
                  </Typography>
                </Grid>
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  direction={'column'}
                  spacing={{ xs: 2 }}
                >
                  <Typography
                    variant="h5"
                    align="center"
                    sx={{
                      typography: { xs: 'h5', md: 'h4' },
                      '@media (min-width:300px)': { fontWeight: 700 },
                    }}
                  >
                    Aktuálny set
                  </Typography>
                  {currentSet && <SetView SetData={currentSet} />}
                </Grid>
              </Stack>
            </Grid>
            <Grid size={4} order={3}>
              <Stack
                direction="column"
                spacing={{ xs: 1 }}
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h5"
                  align="center"
                  sx={{
                    typography: { xs: 'h5', md: 'h4' },
                    '@media (min-width:300px)': { fontWeight: 700 },
                  }}
                >
                  Hráči
                </Typography>
                {data && (
                  <PlayersTable
                    PlayerData={sortPlayersByRatio(filterPlayersWithTeam(data.players))}
                    Pagination={[15, 25, 50]}
                    SetPlanning={false}
                  />
                )}
              </Stack>
            </Grid>
          </Grid>
        </PageContainer>
      </DashboardLayout>
    </AppProvider>
  );
}
